// src/components/DlPanel.tsx (v2.4 — 2026-05-22 本番運用化)
// ダウンロードタブ: 集計データダウンロード (fact_long_all subset、 xlsx 出力)
import { useState } from "react";
import { useFilters } from "../store";
import { api } from "../lib/api";
import { downloadXlsx } from "../lib/xlsx";
import { ymJp } from "../lib/format";
import type { MetricRow } from "@shared/api";

interface Props { metrics: MetricRow[]; }

export function DlPanel({ metrics }: Props) {
  const f = useFilters();
  const [busy, setBusy] = useState<string | null>(null);

  void metrics;
  const fyShort = `${f.periodFrom.slice(0,7)}_${f.periodTo.slice(0,7)}`;

  async function dlDetail(allCross: boolean) {
    setBusy("detail");
    try {
      const res = await api.detail({
        // allCross=true: 全粒度 (大/中/小) × 全医療区分 × 全予定緊急
        granularity: (allCross ? "全" : f.granularity) as typeof f.granularity,
        iryo:  allCross ? "全" : f.iryo,
        yotei: allCross ? "全" : f.yotei,
        periodFrom: f.periodFrom,
        periodTo: f.periodTo,
      });
      const granLabel  = `診療科粒度${allCross ? "全" : `${f.granularity}分類`}`;
      const iryoLabel  = `医療区分${allCross ? "全" : f.iryo}`;
      const yoteiLabel = `予定緊急${allCross ? "全" : f.yotei}`;
      // xlsx 出力時のみ列名を rename (粒度 → 診療科粒度、 date → 年月 (YYYY-MM 切詰))。
      // API レベルで rename すると MetricMatrix / DeptMetricTable / Heatmap 等
      // r.date を直接 split する集計コンポーネントが全部 crash するため
      const xlsxRows = res.rows.map((r) => ({
        "診療科粒度": r["粒度"],
        "年月":       r.date.slice(0, 7),
        "診療科":    r["診療科"],
        "医療区分": r["医療区分"],
        "予定緊急": r["予定緊急"],
        "集計区分": r["集計区分"],
        "実績":     r["実績"],
      }));
      await downloadXlsx(xlsxRows as unknown as Record<string, unknown>[],
                  `集計データ_${granLabel}_${iryoLabel}_${yoteiLabel}_${fyShort}.xlsx`);
    } catch (e) {
      console.error(e);
      alert(`ダウンロード失敗: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      <section className="card p-4">
        <h2 className="text-base font-semibold mb-2">⬇ 集計データダウンロード</h2>
        <p className="text-xs text-slate-500 mb-3">
          現在のフィルタ (期間 {ymJp(f.periodFrom)} 〜 {ymJp(f.periodTo)}, 粒度 {f.granularity}分類)
          で 集計済 fact データを xlsx ダウンロード。
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => dlDetail(false)}
            disabled={busy === "detail"}
            className="border border-slate-300 rounded p-3 text-left hover:bg-slate-50 disabled:opacity-50"
          >
            <div className="font-semibold text-sm">現フィルタ条件で出力</div>
            <div className="text-xs text-slate-500 mt-1">
              粒度={f.granularity}分類, 医療区分={f.iryo}, 予定緊急={f.yotei}<br/>
              全集計区分、 全診療科
            </div>
            {busy === "detail" && <div className="text-xs text-blue-600 mt-1">取得中...</div>}
          </button>

          <button
            onClick={() => dlDetail(true)}
            disabled={busy === "detail"}
            className="border border-slate-300 rounded p-3 text-left hover:bg-slate-50 disabled:opacity-50"
          >
            <div className="font-semibold text-sm">全集計データ</div>
            <div className="text-xs text-slate-500 mt-1">
              全粒度 (大/中/小) × 全医療区分 (合計/急性期/回復期) × 全予定緊急 (合計/予定/緊急)
            </div>
          </button>
        </div>
      </section>
    </div>
  );
}
